import { HttpContextToken, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@shared/services/auth.service';
import { AuthApiService } from '@shared/services/auth-api.service';
import { catchError, switchMap, throwError } from 'rxjs';

/**
 * Marks a retried request to prevent an infinite refresh loop.
 * If a retried request still returns 401, we log out instead of refreshing again.
 */
const IS_RETRY = new HttpContextToken<boolean>(() => false);

/**
 * Auth interceptor — cookie-based authentication.
 *
 * The access token lives in an HttpOnly cookie "at" set by the backend on login/refresh.
 * The browser sends it automatically on every same-site request (withCredentials: true).
 * No Authorization header is ever attached — the token is fully invisible to JavaScript
 * and to the DevTools Network tab.
 *
 * On 401:
 *   1. Calls /auth/refresh-token (browser sends "rt" cookie automatically).
 *   2. Backend rotates refresh token and sets a new "at" cookie.
 *   3. Retries the original request once with the new cookie.
 *   4. If the retry also fails, logs the user out.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const authApi = inject(AuthApiService);

  // Always attach withCredentials so all HttpOnly cookies (at, fgp, rt) are sent.
  const reqWithCreds = req.clone({ withCredentials: true });

  return next(reqWithCreds).pipe(
    catchError((error: HttpErrorResponse) => {
      const isRetry    = req.context.get(IS_RETRY);
      const isAuthPath = req.url.includes('/auth/');

      // Peticiones que traen su propia credencial (portal de autoservicio y cualquier futura
      // superficie para usuarios sin cuenta) no participan del ciclo de refresco: quien las hace no
      // tiene sesión de plataforma que renovar, y un 401 aquí significa «tu credencial caducó», no
      // «hay que cerrar sesión». Sin esta salida, un visitante externo con el enlace caducado
      // acababa expulsado a la pantalla de login de PartnerCore.
      const isSelfCredentialed = req.headers.has('X-Portal-Session');

      // Only attempt silent refresh for protected endpoints on first 401.
      if (error.status === 401 && !isRetry && !isAuthPath && !isSelfCredentialed) {
        return authApi.refreshToken().pipe(
          switchMap((res: any) => {
            authService.slideSession(res.user);
            // Retry the original request — browser will include the new "at" cookie.
            return next(req.clone({
              withCredentials: true,
              context: req.context.set(IS_RETRY, true),
            }));
          }),
          catchError((refreshError: HttpErrorResponse) => {
            // Distinguir el motivo de fin de sesion para el mensaje contextual:
            // tope absoluto, inactividad (idle) o expiracion generica.
            const reason = refreshError.error?.message;
            authService.logout(
              reason === 'SESSION_MAX_REACHED' ? 'session_maxima'
                : reason === 'SESSION_IDLE_EXPIRED' ? 'inactividad'
                  : 'expirada'
            );
            return throwError(() => error);
          })
        );
      }

      return throwError(() => error);
    })
  );
};